package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodMode
import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.Announcement
import br.com.vvrefeicoes.portal.domain.model.AnnouncementAudience
import br.com.vvrefeicoes.portal.domain.model.AnnouncementDraft
import br.com.vvrefeicoes.portal.domain.model.AnnouncementTarget
import br.com.vvrefeicoes.portal.domain.repository.AnnouncementPage
import br.com.vvrefeicoes.portal.domain.repository.AnnouncementRepository
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteAnnouncementRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : AnnouncementRepository {
    override suspend fun loadAnnouncements(): List<Announcement> {
        val response = httpClient.get(baseUrl + "/announcements?size=100") { authorize() }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        return response.body<AnnouncementPageDto>().content.map { it.toDomain() }
    }

    override suspend fun loadAnnouncementsPage(
        page: Int,
        size: Int,
        query: String,
        selection: PeriodSelection,
    ): AnnouncementPage {
        val url =
            buildString {
                append(baseUrl)
                    .append("/announcements?page=")
                    .append(page)
                    .append("&size=")
                    .append(size)
                when (selection.mode) {
                    PeriodMode.MONTH -> append("&period=mensal&year=${selection.month.year}&month=${selection.month.month}")
                    PeriodMode.YEAR -> append("&period=anual&year=${selection.year}")
                    PeriodMode.TOTAL -> Unit
                    PeriodMode.CUSTOM -> append("&period=personalizado&start=${selection.start.id}&end=${selection.end.id}")
                }
                if (query.isNotBlank()) append("&q=").append(query.encodeURLParameter())
            }
        val response = httpClient.get(url) { authorize() }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        val pageDto = response.body<AnnouncementPageDto>()
        return AnnouncementPage(pageDto.content.map { it.toDomain() }, pageDto.page.totalPages)
    }

    override suspend fun create(draft: AnnouncementDraft): Announcement {
        val response =
            httpClient.post(baseUrl + "/announcements") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(draft.toDto())
            }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        return response.body<AnnouncementDto>().toDomain()
    }

    override suspend fun update(
        id: String,
        draft: AnnouncementDraft,
    ): Announcement {
        val response =
            httpClient.put(baseUrl + "/announcements/$id") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(draft.toDto())
            }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        return response.body<AnnouncementDto>().toDomain()
    }

    override suspend fun changeStatus(
        id: String,
        active: Boolean,
    ): Announcement {
        val response =
            httpClient.patch(baseUrl + "/announcements/$id/status") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(AnnouncementStatusDto(active))
            }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        return response.body<AnnouncementDto>().toDomain()
    }

    override suspend fun delete(id: String) {
        val response = httpClient.delete(baseUrl + "/announcements/$id") { authorize() }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
    }

    override suspend fun uploadImage(
        bytes: ByteArray,
        contentType: String,
    ): String {
        val response =
            httpClient.post(baseUrl + "/announcements/images") {
                authorize()
                setBody(
                    MultiPartFormDataContent(
                        formData {
                            append(
                                "file",
                                bytes,
                                Headers.build {
                                    append(HttpHeaders.ContentType, contentType)
                                    append(HttpHeaders.ContentDisposition, "filename=\"imagem\"")
                                },
                            )
                        },
                    ),
                )
            }
        ensureSuccess(response.status.value, response.status.isSuccess(), response.bodyAsText())
        return response.body<AnnouncementImageDto>().url
    }

    private suspend fun ensureSuccess(
        status: Int,
        success: Boolean,
        body: String,
    ) {
        if (success) return
        val message =
            runCatching {
                Json { ignoreUnknownKeys = true }
                    .decodeFromString<PanelApiErrorDto>(body)
                    .message
            }.getOrNull()
        error(message ?: "HTTP $status")
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class AnnouncementPageDto(
    val content: List<AnnouncementDto> = emptyList(),
    val page: AnnouncementPageMetadataDto = AnnouncementPageMetadataDto(),
)

@Serializable
private data class AnnouncementPageMetadataDto(
    val totalPages: Int = 1,
)

@Serializable
private data class AnnouncementDto(
    val id: String = "",
    val title: String = "",
    val body: String = "",
    val priority: String = "LOW",
    val imageUrl: String? = null,
    val active: Boolean = true,
    val publishedAt: String? = null,
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val targets: List<AnnouncementTargetDto> = emptyList(),
)

@Serializable
private data class AnnouncementTargetDto(
    val targetType: String = "",
    val targetRef: String = "",
)

@Serializable
private data class AnnouncementTargetRefDto(
    val targetType: String,
    val targetRef: String,
)

@Serializable
private data class AnnouncementDraftDto(
    val title: String,
    val body: String,
    val priority: String,
    val imageUrl: String?,
    val active: Boolean,
    val targets: List<AnnouncementTargetRefDto>,
)

@Serializable
private data class AnnouncementStatusDto(
    val active: Boolean,
)

@Serializable
private data class AnnouncementImageDto(
    val url: String = "",
)

private fun AnnouncementDto.toDomain(): Announcement =
    Announcement(
        id = id,
        title = title,
        body = body,
        priority = priority.toPriority(),
        imageUrl = imageUrl,
        active = active,
        publishedAt = publishedAt,
        createdAt = createdAt,
        updatedAt = updatedAt,
        targets = targets.mapNotNull { it.toDomain() },
    )

private fun AnnouncementTargetDto.toDomain(): AnnouncementTarget? {
    val audience = AnnouncementAudience.entries.firstOrNull { it.name == targetType } ?: return null
    return AnnouncementTarget(audience, targetRef)
}

private fun AnnouncementDraft.toDto(): AnnouncementDraftDto =
    AnnouncementDraftDto(
        title = title,
        body = body,
        priority = priority.name,
        imageUrl = imageUrl,
        active = active,
        targets = targets.map { AnnouncementTargetRefDto(it.type.name, it.ref) },
    )
